import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Box, Skeleton } from '@mui/material';

import { DevelopmentSelect } from '@modules/_shared/components/DevelopmentSelect';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { UPDATE_LISTING } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

interface ListingDevelopmentCardProps {
  listingId: string;
  developmentId?: string | null;
  shouldBeOpen?: boolean;
  readOnly?: boolean;
}

export const ListingDevelopmentCard: React.FC<ListingDevelopmentCardProps> = ({
  listingId,
  developmentId,
  shouldBeOpen: shouldBeOpenProp = false,
  readOnly = false,
}) => {
  // Open if prop is true AND if a development is selected
  const shouldBeOpen = shouldBeOpenProp && developmentId != null;
  const { t } = useLocale();

  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const [updateListing, { loading }] = useMutation(UPDATE_LISTING, {
    onError: setErrorLike,
  });

  const handleChange = useCallback(
    (developmentId: string | null) => {
      updateListing({
        variables: {
          id: listingId,
          lot: {
            development_id: developmentId,
          },
        },
      });
    },
    [listingId, updateListing],
  );

  return (
    <ListingAccordion title={t('Development')} defaultExpanded={shouldBeOpen}>
      <Box>
        <DevelopmentSelect
          developmentId={developmentId}
          onChange={handleChange}
          autoFocus={false}
          loading={loading}
          disabled={loading || readOnly}
          InputProps={{
            placeholder: t('Select development'),
          }}
        />
      </Box>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </ListingAccordion>
  );
};

export const ListingDevelopmentCardSkeleton: React.FC<{
  shouldBeOpen?: boolean;
}> = ({ shouldBeOpen }) => {
  const { t } = useLocale();
  return (
    <ListingAccordion title={t('Development')} defaultExpanded={shouldBeOpen}>
      <Box>
        <Skeleton variant="rectangular" height={40} />
      </Box>
    </ListingAccordion>
  );
};
